/* --------------------------------------------------------------------------
   Excel-fil (.xlsx) för exporterna

   En .xlsx-fil är en zip med några XML-filer. Den här modulen skriver
   XML-filerna; ui/export.js packar dem med JSZip, som sidan redan har för
   att läsa zip-exporter. Inget nytt bibliotek och inget som hämtas utifrån.

   Varje kolumn har en typ, så att Excel får riktiga tal och datum:
     text  text, som den står (artikel- och kundnummer behåller nollor först)
     int   heltal, med tusentalsavgränsare
     num   tal med två decimaler, med tusentalsavgränsare (belopp, antal)
     pct   procent som tal med en decimal (35,2 i en kolumn som heter "%")
     date  "2026-03-12" blir ett Excel-datum, visat som 2026-03-12
   Talen avrundas till det som visas, så att 0,1 + 0,2 inte blir
   0,30000000000000004 i cellen. Saknas ett värde (null, NaN) blir cellen tom.

   Text skrivs som inlineStr, aldrig som formel: en artikel som heter
   "=SUMMA(A1)" står som text. Rubrikraden är fet, fryst och har filter.
   -------------------------------------------------------------------------- */

const STYLE = {header: 1, int: 2, num: 3, pct: 4, date: 5};
const DECIMALS = {int: 0, num: 2, pct: 1};

/** XML 1.0 tillåter inte styrtecken utom tab, radbrytning och vagnretur. */
const INVALID = /[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g;
export const xmlEscape = s => String(s).replace(INVALID, '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Kolumnnamn: 0 -> A, 25 -> Z, 26 -> AA. */
export function colName(i){
  let s = '';
  for(i += 1; i > 0; i = Math.floor((i - 1) / 26)) s = String.fromCharCode(65 + (i - 1) % 26) + s;
  return s;
}

/** "2026-03-12" -> Excels dagnummer (1900-systemet), räknat i UTC. */
export function excelDate(iso){
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? ''));
  if(!m) return null;
  const ms = Date.UTC(+m[1], +m[2] - 1, +m[3]);
  // Datumet måste finnas: 2026-02-30 blir inte 2 mars
  if(new Date(ms).getUTCDate() !== +m[3]) return null;
  return ms / 86400000 + 25569;
}

/** Ett bladnamn Excel godtar: högst 31 tecken, utan : \ / ? * [ ]. */
export function sheetName(s){
  const t = String(s ?? '').replace(/[:\\/?*[\]]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 31);
  return t || 'Blad1';
}

function cellXml(ref, type, v){
  if(type === 'text'){
    if(v == null || v === '') return '';
    const t = xmlEscape(String(v).slice(0, 32767));
    return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${t}</t></is></c>`;
  }
  const n = type === 'date' ? excelDate(v) : (typeof v === 'number' ? v : null);
  if(n == null || !Number.isFinite(n)) return '';
  const d = DECIMALS[type];
  const val = d == null ? n : Math.round(n * 10 ** d) / 10 ** d;
  return `<c r="${ref}" s="${STYLE[type]}"><v>${val}</v></c>`;
}

/** Ungefärlig visad bredd, för kolumnbredden. */
function shown(type, v){
  if(v == null || v === '') return 0;
  if(type === 'text') return String(v).length;
  if(type === 'date') return 10;
  if(typeof v !== 'number' || !Number.isFinite(v)) return 0;
  const d = DECIMALS[type] ?? 0;
  const digits = Math.abs(v).toFixed(d).length;
  return digits + Math.floor((Math.max(1, Math.abs(v)).toFixed(0).length - 1) / 3) + (v < 0 ? 1 : 0);
}

/**
 * XML-filerna i en arbetsbok med ett blad.
 * @param {{sheet: string, columns: {h: string, t?: string}[], rows: any[][]}} opts
 * @returns {Record<string, string>} sökväg i zip-filen -> innehåll
 */
export function xlsxParts({sheet, columns, rows}){
  const types = columns.map(c => c.t || 'text');
  const head = '<row r="1">' + columns.map((c, i) =>
    `<c r="${colName(i)}1" t="inlineStr" s="${STYLE.header}"><is><t xml:space="preserve">${xmlEscape(c.h)}</t></is></c>`).join('') + '</row>';
  const body = rows.map((r, ri) => {
    const n = ri + 2;
    return `<row r="${n}">` + types.map((t, ci) => cellXml(colName(ci) + n, t, r[ci])).join('') + '</row>';
  }).join('');

  const widths = columns.map((c, i) => {
    let w = c.h.length + 2;      // filterpilen tar plats i rubriken
    for(const r of rows) w = Math.max(w, shown(types[i], r[i]));
    return Math.min(60, Math.max(8, w + 2));
  });
  const cols = '<cols>' + widths.map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`).join('') + '</cols>';
  const last = colName(columns.length - 1) + (rows.length + 1);

  const worksheet = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>' +
    '<sheetFormatPr defaultRowHeight="15"/>' + cols +
    `<sheetData>${head}${body}</sheetData>` +
    `<autoFilter ref="A1:${last}"/>` +
    '</worksheet>';

  const styles = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    '<numFmts count="2"><numFmt numFmtId="164" formatCode="0.0"/><numFmt numFmtId="165" formatCode="yyyy-mm-dd"/></numFmts>' +
    '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>' +
    '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>' +
    '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
    '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
    '<cellXfs count="6">' +
      '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
      '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>' +
      '<xf numFmtId="3" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
      '<xf numFmtId="4" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
      '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
      '<xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
    '</cellXfs>' +
    '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
    '</styleSheet>';

  const name = xmlEscape(sheetName(sheet));
  return {
    '[Content_Types].xml': '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
      '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
      '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
      '<Default Extension="xml" ContentType="application/xml"/>' +
      '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
      '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
      '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
      '</Types>',
    '_rels/.rels': '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
      '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
      '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
      '</Relationships>',
    'xl/workbook.xml': '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
      '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
      `<sheets><sheet name="${name}" sheetId="1" r:id="rId1"/></sheets>` +
      // Filtret på rubrikraden behöver ett dolt namn, annars tar Excel bort det
      `<definedNames><definedName name="_xlnm._FilterDatabase" localSheetId="0" hidden="1">'${name.replace(/'/g, "''")}'!$A$1:$${last.replace(/(\d+)$/, '$$$1')}</definedName></definedNames>` +
      '</workbook>',
    'xl/_rels/workbook.xml.rels': '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
      '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
      '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>' +
      '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>' +
      '</Relationships>',
    'xl/styles.xml': styles,
    'xl/worksheets/sheet1.xml': worksheet
  };
}

export const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
